import React, {FC} from "react";
import {Button} from "antd";
import {AssistantLessonViewProps} from "./AssistantLargeTiles";
import {formatDayNumber, formatWeekdayShort, formatTimeRange, groupLessonsByMonth} from "./assistantDateUtils";
import styles from "./assistantApp.less";

/**
 * Timeline view for assistant lesson assignments.
 * Lessons are grouped by month, then by day.
 * Each event gets its own dot on the timeline.
 */
const AssistantSmallTiles: FC<AssistantLessonViewProps> = ({
    lessons,
    pendingLessonId,
    onAssign,
    onUnassign,
}) => {
    const monthGroups = groupLessonsByMonth(lessons);

    return (
        <div className={styles.assistantTimeline}>
            {monthGroups.map((month) => (
                <div key={month.monthLabel}>
                    <div className={styles.assistantTimelineMonthHeader}>
                        {month.monthLabel}
                    </div>
                    <div className={styles.assistantTimelineMonth}>
                        {month.dayGroups.map((dayGroup, dayIdx) =>
                            dayGroup.lessons.map((item, itemIdx) => {
                                const lesson = item.courseLesson;
                                const isFirstOfDay = itemIdx === 0;
                                const isLastOfDay = itemIdx === dayGroup.lessons.length - 1;
                                const isFirstItem = isFirstOfDay;
                                const isLastItem = isLastOfDay;
                                const courseLabel = [
                                    lesson.course?.courseType?.name,
                                    lesson.course?.prefixedCourseNumber,
                                ]
                                    .filter(Boolean)
                                    .join(" \u00B7 ");
                                const maxSlots =
                                    item.assistantSlotsMax && item.assistantSlotsMax > 0
                                        ? item.assistantSlotsMax
                                        : null;
                                const isFull =
                                    maxSlots !== null && item.assistantSlotsUsed >= maxSlots;
                                const isDisabled = !item.isAssignedToMe && isFull;

                                const cardClasses = [
                                    styles.assistantTimelineCard,
                                    item.isAssignedToMe
                                        ? styles.assistantTimelineCardAssigned
                                        : "",
                                    isFull && !item.isAssignedToMe
                                        ? styles.assistantTimelineCardFull
                                        : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");

                                const dotClasses = [
                                    styles.assistantTimelineDot,
                                    item.isAssignedToMe
                                        ? styles.assistantTimelineDotActive
                                        : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ");

                                return (
                                    <div
                                        key={lesson.id}
                                        className={
                                            styles.assistantTimelineRow +
                                            (isFirstItem ? " " + styles.assistantTimelineRowFirst : "") +
                                            (isLastItem ? " " + styles.assistantTimelineRowLast : "")
                                        }
                                    >
                                        <div className={styles.assistantTimelineDateCol}>
                                            {isFirstOfDay && (
                                                <>
                                                    <div className={styles.assistantTimelineDayNumber}>
                                                        {formatDayNumber(dayGroup.date)}
                                                    </div>
                                                    <div className={styles.assistantTimelineDayName}>
                                                        {formatWeekdayShort(dayGroup.date)}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className={styles.assistantTimelineDotCol}>
                                            <div className={dotClasses} />
                                        </div>
                                        <div className={styles.assistantTimelineCardCol}>
                                            <div className={cardClasses}>
                                                <div className={styles.assistantTimelineCardInfo}>
                                                    <div className={styles.assistantTimelineCardTitle}>
                                                        {courseLabel || "Kurseinheit"}
                                                    </div>
                                                    <div className={styles.assistantTimelineCardTime}>
                                                        {formatTimeRange(
                                                            lesson.startDateTime,
                                                            lesson.endDateTime
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={styles.assistantTimelineCardAction}>
                                                    {isFull && !item.isAssignedToMe ? (
                                                        <span
                                                            className={
                                                                styles.assistantTimelineCardFullLabel
                                                            }
                                                        >
                                                            {"voll"}
                                                        </span>
                                                    ) : (
                                                        <Button
                                                            type={
                                                                item.isAssignedToMe
                                                                    ? "default"
                                                                    : "primary"
                                                            }
                                                            size="small"
                                                            disabled={isDisabled}
                                                            loading={pendingLessonId === lesson.id}
                                                            className={item.isAssignedToMe ? styles.assistantBtnUnassign : undefined}
                                                            style={{borderRadius: 6}}
                                                            onClick={() => {
                                                                if (item.isAssignedToMe) {
                                                                    onUnassign(lesson.id);
                                                                } else {
                                                                    onAssign(lesson.id);
                                                                }
                                                            }}
                                                        >
                                                            {item.isAssignedToMe
                                                                ? "Austragen"
                                                                : "Eintragen"}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AssistantSmallTiles;
